import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, merge, Observable } from 'rxjs';
import {
    auditTime,
    catchError,
    filter,
    map,
    switchMap,
    withLatestFrom,
} from 'rxjs/operators';
import { User } from '@auth0/auth0-angular';
import {
    BiddingService,
    GetMyWatchedItems200Response,
} from '@viewbid/ts-angular-client';
import {
    loadWatchlist,
    loadWatchlistSuccess,
    loadWatchlistFailure,
    refreshWatchlistCount,
    watchlistUserChanged,
} from './watchlist-page-state.actions';
import { Action, Store } from '@ngrx/store';
import { SocketService } from 'src/app/viewbid/shared/services/socket.service';
import { selectUser } from 'src/app/viewbid/state/app-selectors';
import { AppState } from 'src/app/viewbid/state/app-state';

export const WATCHLIST_COUNT_REFRESH_MS: number = 1000;

const WATCHED_AUCTION_CLOSED: string = 'WATCHED_AUCTION_CLOSED';

function isWatchedAuctionClosedEvent(message: string): boolean {
    try {
        const event: { action?: unknown } = JSON.parse(message);
        return event.action === WATCHED_AUCTION_CLOSED;
    } catch {
        return false;
    }
}

@Injectable()
export class WatchlistPageStateEffects {
    /**
     * Effect to load all watchers from the watchlist using pagination.
     * It listens for the loadWatchlist action, calls the BiddingService to fetch data,
     * and maps the response to either a success or failure action.
     */
    loadWatchlist$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(loadWatchlist),
            switchMap(
                ({
                    page,
                    pageSize,
                    query,
                }: {
                    page: number;
                    pageSize: number;
                    query?: string;
                }) =>
                    this.biddingService
                        .getMyWatchedItems(page, pageSize, query)
                        .pipe(
                            map((response: GetMyWatchedItems200Response) =>
                                loadWatchlistSuccess({ response }),
                            ),
                            catchError((error: Error) =>
                                of(loadWatchlistFailure({ error })),
                            ),
                        ),
            ),
        ),
    );

    refreshWatchlistCount$: Observable<Action> = createEffect(() =>
        this.actions$.pipe(
            ofType(refreshWatchlistCount, watchlistUserChanged),
            map(() => loadWatchlist({ page: 1, pageSize: 1 })),
        ),
    );

    refreshCountOnLiveChange$: Observable<Action> = createEffect(() =>
        merge(
            this.socketService.resource$.pipe(
                filter(isWatchedAuctionClosedEvent),
            ),
            this.socketService.reconnected$,
        ).pipe(
            auditTime(WATCHLIST_COUNT_REFRESH_MS),
            withLatestFrom(this.store.select(selectUser)),
            filter(([, user]: [unknown, User | null]) =>
                Boolean(user?.['accountMappingsId']),
            ),
            map(() => refreshWatchlistCount()),
        ),
    );

    constructor(
        private actions$: Actions,
        private biddingService: BiddingService,
        private socketService: SocketService,
        private store: Store<AppState>,
    ) {}
}
